import { skipToken } from "@reduxjs/toolkit/query/react";
import { useGetAnswersWithAttorneyRestrictionsQuery } from "../api/answers";
import {
  getAttorneyServicesData,
  getRepresentationTextAndQuantityBasedOnRestrictions,
  getSectionToReturn,
} from "./layout/utils/attorney-services";
import { useGetProductsQuery } from "../api/payments";
import styled from "@emotion/styled";
import {
  BORDER_RADIUS_DEFAULT,
  COLOR_TERTIARY,
  IconSize,
  INFORMATIONAL_MESSAGE_POPUP_BACKGROUND_COLOR_GRADIENT,
  MOBILE_SCREEN,
} from "./theme";
import IconContainer from "./icons/IconContainer";
import InformationIcon from "./icons/InformationIcon";
import { Paragraph, ParagraphHeaderXLarge } from "./typography/Paragraph";
import Checkbox from "./icons/Checkbox";
import { PrimaryButtonSmallNormalWeight } from "./buttons/PrimaryButton";
import { SecondaryButtonSmallNormalWeight } from "./buttons/SecondaryButton";
import { useNavigate } from "react-router";
import { useGetQuestionnaireSectionsQuery } from "../api/questionnaire";
import useGetCurrentUserDocument from "../hooks/useGetCurrentUserDocument";

const RepresentationBlockerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 104px;
  background: ${INFORMATIONAL_MESSAGE_POPUP_BACKGROUND_COLOR_GRADIENT};
  border-radius: ${BORDER_RADIUS_DEFAULT};
  gap: 24px;
  ${MOBILE_SCREEN} {
    padding: 32px 16px;
  }
`;
const StyledIconContainer = styled(IconContainer)`
  border-radius: 50%;
`;

const BenefitsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`;

const RowContainer = styled.div<{ changeDirection?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;
  ${({ changeDirection }) =>
    changeDirection
      ? `
      ${MOBILE_SCREEN} {
        flex-direction: column-reverse;
      }
    `
      : ""}
`;

const StyledPrimaryButton = styled(PrimaryButtonSmallNormalWeight)`
  flex: 1.4;
`;

const StyledSecondaryButton = styled(SecondaryButtonSmallNormalWeight)`
  flex: 0.6;
`;

const RepresentationBlocker = () => {
  const navigate = useNavigate();
  const {
    user,
    document,
    isLoading: isUserLoading,
    error: userError,
  } = useGetCurrentUserDocument();

  const {
    data: sections,
    isLoading: isSectionsLoading,
    isError: isSectionsError,
  } = useGetQuestionnaireSectionsQuery(
    user?.id ? { id: user.id, includeStatus: true } : skipToken,
  );

  const {
    data: products,
    isLoading: isProductsLoading,
    error: isProductsError,
  } = useGetProductsQuery();

  const {
    data: answersWithAttorneyRestrictions,
    isLoading: isAnswersWithAttorneyRestrictionsLoading,
    error: answersWithAttorneyRestrictionsError,
  } = useGetAnswersWithAttorneyRestrictionsQuery(
    user && document?.stateId && !isUserLoading && !userError
      ? { userId: user.id }
      : skipToken,
  );

  const isLoading =
    isUserLoading ||
    isAnswersWithAttorneyRestrictionsLoading ||
    isProductsLoading ||
    isSectionsLoading;
  const error =
    userError ??
    answersWithAttorneyRestrictionsError ??
    isProductsError ??
    isSectionsError;
  const missingData =
    !user ||
    !document ||
    !answersWithAttorneyRestrictions ||
    !products ||
    !sections;

  if (isLoading || error || missingData) {
    return null;
  }

  const representationService = getAttorneyServicesData(
    document.state,
    products,
  ).REPRESENTATION;
  const { serviceDescription } =
    getRepresentationTextAndQuantityBasedOnRestrictions(
      representationService,
      answersWithAttorneyRestrictions,
      document.stateId,
    );

  const sectionToReturn = getSectionToReturn(
    sections,
    answersWithAttorneyRestrictions,
    document.stateId,
  );

  const handleChangeAnswer = () => {
    void navigate(sectionToReturn?.path ?? "/questionnaire");
  };

  const handleBookAttorney = () => {
    void navigate("/attorney-services?serviceType=REPRESENTATION");
  };

  return (
    <RepresentationBlockerContainer data-testid="representation-blocker">
      <StyledIconContainer
        backgroundColor={COLOR_TERTIARY}
        width="56px"
        height="56px"
      >
        <InformationIcon />
      </StyledIconContainer>
      <ParagraphHeaderXLarge>Book your attorney first</ParagraphHeaderXLarge>
      <Paragraph>
        {serviceDescription}
        <br />
        <br />
        Benefits of working with a Helloprenup attorney:
      </Paragraph>
      <BenefitsContainer>
        <RowContainer>
          <IconContainer backgroundColor="none" width="16px" height="16px">
            <Checkbox isChecked={true} size={IconSize.S} />
          </IconContainer>
          <Paragraph>State-specific and love helping couples</Paragraph>
        </RowContainer>
        <RowContainer>
          <IconContainer backgroundColor="none" width="16px" height="16px">
            <Checkbox isChecked={true} size={IconSize.S} />
          </IconContainer>
          <Paragraph>
            Save, on average, $8,000 when you hire a HelloPrenup attorney
          </Paragraph>
        </RowContainer>
        <RowContainer>
          <IconContainer backgroundColor="none" width="16px" height="16px">
            <Checkbox isChecked={true} size={IconSize.S} />
          </IconContainer>
          <Paragraph>Complete entire agreement in your account</Paragraph>
        </RowContainer>
        {document.state.allowsOnlineNotary && (
          <RowContainer>
            <IconContainer backgroundColor="none" width="16px" height="16px">
              <Checkbox isChecked={true} size={IconSize.S} />
            </IconContainer>
            <Paragraph>Includes e-sign and online notarization</Paragraph>
          </RowContainer>
        )}
      </BenefitsContainer>
      <RowContainer changeDirection={true}>
        <StyledSecondaryButton
          onClick={handleChangeAnswer}
          data-testid="change-answer-button"
        >
          Change my answer
        </StyledSecondaryButton>
        <StyledPrimaryButton
          onClick={handleBookAttorney}
          data-testid="book-attorney-button"
        >
          Book your attorney
        </StyledPrimaryButton>
      </RowContainer>
    </RepresentationBlockerContainer>
  );
};

export default RepresentationBlocker;
